import { createContext, useContext, useState, ReactNode, ComponentType, useCallback } from 'react'
import L from 'leaflet'

// Generic layer interface
export interface MapLayer {
  id: string
  name: string
  type: 'tile' | 'marker' | 'geojson' | 'custom'
  visible: boolean
  opacity?: number
  zIndex?: number
  metadata?: Record<string, any>
  
  // For tile layers
  tileConfig?: {
    url: string
    bounds?: L.LatLngBounds
    attribution?: string
    minZoom?: number
    maxZoom?: number
  }
  
  // For marker/custom layers - component to render
  component?: ComponentType<any>
  componentProps?: Record<string, any>
}

interface MapLayersContextType {
  layers: MapLayer[]
  
  // Generic layer operations
  addLayer: (layer: MapLayer) => void
  removeLayer: (layerId: string) => void
  updateLayer: (layerId: string, updates: Partial<MapLayer>) => void
  toggleLayer: (layerId: string) => void
  setLayerOpacity: (layerId: string, opacity: number) => void
  
  // Get layers by type
  getLayersByType: (type: MapLayer['type']) => MapLayer[]
  getVisibleLayers: () => MapLayer[]
}

const MapLayersContext = createContext<MapLayersContextType | undefined>(undefined)

export function MapLayersProvider({ children }: { children: ReactNode }) {
  const [layers, setLayers] = useState<MapLayer[]>([])

  // Add a new layer - useCallback to stabilize reference
  const addLayer = useCallback((layer: MapLayer) => {
    setLayers((prev) => {
      // Check if layer with same ID exists
      const exists = prev.some((l) => l.id === layer.id)
      if (exists) {
        return prev // Silently skip if already exists
      }
      return [...prev, layer]
    })
  }, [])

  // Remove a layer
  const removeLayer = useCallback((layerId: string) => {
    setLayers((prev) => prev.filter((layer) => layer.id !== layerId))
  }, [])

  // Update layer properties
  const updateLayer = useCallback((layerId: string, updates: Partial<MapLayer>) => {
    setLayers((prev) =>
      prev.map((layer) =>
        layer.id === layerId ? { ...layer, ...updates } : layer
      )
    )
  }, [])

  // Toggle layer visibility
  const toggleLayer = useCallback((layerId: string) => {
    setLayers((prev) =>
      prev.map((layer) =>
        layer.id === layerId ? { ...layer, visible: !layer.visible } : layer
      )
    )
  }, [])

  // Set layer opacity
  const setLayerOpacity = useCallback((layerId: string, opacity: number) => {
    setLayers((prev) =>
      prev.map((layer) =>
        layer.id === layerId ? { ...layer, opacity } : layer
      )
    )
  }, [])

  // Get layers by type
  const getLayersByType = useCallback((type: MapLayer['type']) => {
    return layers.filter((layer) => layer.type === type)
  }, [layers])

  // Get all visible layers
  const getVisibleLayers = useCallback(() => {
    return layers.filter((layer) => layer.visible)
  }, [layers])

  const value: MapLayersContextType = {
    layers,
    addLayer,
    removeLayer,
    updateLayer,
    toggleLayer,
    setLayerOpacity,
    getLayersByType,
    getVisibleLayers,
  }

  return (
    <MapLayersContext.Provider value={value}>
      {children}
    </MapLayersContext.Provider>
  )
}

export function useMapLayers() {
  const context = useContext(MapLayersContext)
  if (context === undefined) {
    throw new Error('useMapLayers must be used within a MapLayersProvider')
  }
  return context
}
